import React from 'react';
import { useTable, Column } from 'react-table';

import { useAppSelector } from '../../../../app/hooks';
import { PunchCardsForAllDrivers } from '../../../../models';

import './PunchCardsTable.scss';

type ColumnsType = PunchCardsForAllDrivers;

export interface PunchCardsTableProps {
  onViewDriverDetails:  React.Dispatch<React.SetStateAction<PunchCardsForAllDrivers | undefined>>;
}

export function PunchCardsTable(props: PunchCardsTableProps) {
  const { onViewDriverDetails } = props;

  const punchCardSummaries = useAppSelector(state => state.punchCards.punchCardSummaries);
  const payPeriodSummaries = useAppSelector(state => state.punchCards.payPeriodSummaries);

  const formatDecimalHours = (minutes: number) => `${(minutes / 60).toFixed(2)} hrs`;

  const getDriverPayPeriodSummary = (driverId: string) => {
    return payPeriodSummaries?.find(s => s.driverId === driverId);
  };

  const renderActionsCell = (props: any) => {
    return (
      <div className='view-button' onClick={onViewDetailsClick(props.row.original)}>View Details</div>
    );
  };

  const renderTotalCell = (props: any) => {
    const summary = getDriverPayPeriodSummary(props.row.original.driver.id);
    if (!summary) return <span>-</span>;
    return <span>{formatDecimalHours(summary.totalMinutes)}</span>;
  };

  const renderRegularCell = (props: any) => {
    const summary = getDriverPayPeriodSummary(props.row.original.driver.id);
    if (!summary) return <span>-</span>;
    return <span>{formatDecimalHours(summary.totalRegularMinutes)}</span>;
  };

  const renderOvertimeCell = (props: any) => {
    const summary = getDriverPayPeriodSummary(props.row.original.driver.id);
    if (!summary) return <span>-</span>;
    const hasOvertime = summary.totalOvertimeMinutes > 0;
    return (
      <span className={hasOvertime ? 'overtime' : ''}>
        {formatDecimalHours(summary.totalOvertimeMinutes)}
      </span>
    );
  };

  const onViewDetailsClick = (driverSummary: PunchCardsForAllDrivers) => () => {
    onViewDriverDetails(driverSummary);
  }

  const columns: Array<Column<ColumnsType>> = React.useMemo(
    () => [
      {
        Header: 'Driver',
        accessor: cell => `${cell.driver.firstName} ${cell.driver.lastName}`
      },
      {
        Header: 'Regular',
        Cell: renderRegularCell
      },
      {
        Header: 'Overtime',
        Cell: renderOvertimeCell
      },
      {
        Header: 'Total',
        Cell: renderTotalCell
      },
      {
        Header: 'Actions',
        width: 80,
        maxWidth: 80,
        Cell: renderActionsCell
      }
    ],
    [payPeriodSummaries]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable({ columns, data: punchCardSummaries });

  if (punchCardSummaries.length === 0) return (
    <div className='punch-cards-table-component empty-state'>
      <div className='text'>There are no punch card summaries.</div>
    </div>
  );

  return (
    <div className='punch-cards-table-component'>
      <table {...getTableProps()} className='new-table'>
        <thead>
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                <th {...column.getHeaderProps({
                  style: { minWidth: column.minWidth, width: column.width },
                })}>
                  <div className='header-cell'>
                    {column.render('Header')}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map(row => {
            prepareRow(row)
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map(cell => {
                  return (
                    <td {...cell.getCellProps()}>
                      {cell.render('Cell')}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  );
}
